import { useMemo, useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Tooltip } from '@material-ui/core';
import { CheckCircleIconGreen } from '@setvi/shared/components/sicons';

type DisplayState = 'idle' | 'saving' | 'saved' | 'error';

interface SavingStatusProps {
  isLoading: boolean;
  hasError: boolean;
}

const SAVED_TEXT_DISPLAY_MS = 5000;
const ERROR_DISPLAY_MS = 3000;

// Color themes for each state
const stateColors: Record<DisplayState, { bg: string; text: string }> = {
  idle: { bg: '#e8f5e9', text: '#2e7d32' },
  saving: { bg: '#e3f2fd', text: '#1976d2' },
  saved: { bg: '#e8f5e9', text: '#2e7d32' },
  error: { bg: '#ffebee', text: '#c62828' }
};

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Unified container with smooth color transitions
const StatusContainer = styled.div<{
  $state: DisplayState;
  $expanded: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    max-width 0.5s ease;
  max-width: ${({ $expanded }) => ($expanded ? '200px' : '32px')};

  ${({ $state }) => {
    const colors = stateColors[$state];
    return css`
      background-color: ${colors.bg};
      color: ${colors.text};
    `;
  }}
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #90caf9;
  border-top-color: #1976d2;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const StatusText = styled.span<{ $visible: boolean }>`
  white-space: nowrap;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.4s ease;
`;

const IconWrapper = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  position: absolute;
`;

const IconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const formatLastSaved = (date: Date | null): string => {
  if (!date) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 5) return 'Just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const SavingStatus = ({ isLoading, hasError }: SavingStatusProps) => {
  const [displayState, setDisplayState] = useState<DisplayState>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showText, setShowText] = useState(false);

  const wasLoadingRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const innerTimerRef = useRef<NodeJS.Timeout | null>(null);

  const formattedTime = useMemo(
    () => formatLastSaved(lastSaved),
    // Update time display periodically
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastSaved, Math.floor(Date.now() / 10000)]
  );

  // Clear all pending timers to prevent memory leaks
  const clearTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (innerTimerRef.current) {
      clearTimeout(innerTimerRef.current);
      innerTimerRef.current = null;
    }
  };

  // Handle state transitions based on loading/error props
  useEffect(() => {
    clearTimers();

    if (isLoading) {
      setDisplayState('saving');
      setShowText(true);
      wasLoadingRef.current = true;
      return undefined;
    }

    // Not loading - check if we just finished
    if (!wasLoadingRef.current) return undefined;
    wasLoadingRef.current = false;

    if (hasError) {
      setDisplayState('error');
      timerRef.current = setTimeout(
        () => setDisplayState('idle'),
        ERROR_DISPLAY_MS
      );
      return clearTimers;
    }

    // Successfully finished saving
    setDisplayState('saved');
    setLastSaved(new Date());
    setShowText(true);

    timerRef.current = setTimeout(() => {
      setShowText(false);
      // Track the inner timeout to prevent memory leaks on unmount
      innerTimerRef.current = setTimeout(() => {
        setDisplayState('idle');
      }, 700);
    }, SAVED_TEXT_DISPLAY_MS);

    return clearTimers;
  }, [isLoading, hasError]);

  // Nothing to show
  if (displayState === 'idle') {
    return null;
  }

  // Error state (separate container since it's a different flow)
  if (displayState === 'error') {
    return (
      <StatusContainer $state="error" $expanded>
        <StatusText $visible>Save failed</StatusText>
      </StatusContainer>
    );
  }

  const isSaving = displayState === 'saving';
  const currentState = isSaving ? 'saving' : 'saved';
  const statusText = isSaving ? 'Saving...' : `Saved ${formattedTime}`;

  return (
    <Tooltip
      title={`Last saved: ${formattedTime}`}
      arrow
      placement="bottom"
      disableHoverListener={showText || isSaving || !lastSaved}>
      <StatusContainer $state={currentState} $expanded={showText}>
        <IconContainer>
          <IconWrapper $visible={isSaving}>
            <Spinner />
          </IconWrapper>
          <IconWrapper $visible={!isSaving}>
            <CheckCircleIconGreen fontSize="small" />
          </IconWrapper>
        </IconContainer>
        <StatusText $visible={showText}>{statusText}</StatusText>
      </StatusContainer>
    </Tooltip>
  );
};

export default SavingStatus;
